import React, { useEffect, useRef, useState } from 'react';

const useCountUp = (end, duration = 1200, start = false) => {
  const [value, setValue] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!start) return;
    const startTime = performance.now();
    const animate = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * end));
      if (progress < 1) rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [start, end, duration]);

  return value;
};

const stats = [
  { value: 9, suffix: '+', label: 'Live Projects' },
  { value: 10, suffix: '+', label: 'Delivered Overall' },
  { value: 10, suffix: '+', label: 'Happy Clients' },
  { value: 3, suffix: '', label: 'Years Building' },
];

const tickerItems = [
  'Mayura Travels', 'Megha Super Structures', 'Omex Technology Inc', 'Cloudtris',
  'OpenGen', 'Sibaranjan.cloud', 'Mob Services', 'Quality Billing', 'Internal Billing',
];

const ProjectsMain = () => {
  const [inView, setInView] = useState(false);
  const heroRef = useRef(null);

  const liveCount = useCountUp(stats[0].value, 1100, inView);
  const deliveredCount = useCountUp(stats[1].value, 1100, inView);
  const clientsCount = useCountUp(stats[2].value, 1100, inView);
  const yearsCount = useCountUp(stats[3].value, 1100, inView);
  const countedValues = [liveCount, deliveredCount, clientsCount, yearsCount];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );
    if (heroRef.current) observer.observe(heroRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className={`pjh2-hero ${inView ? 'is-in' : ''}`} ref={heroRef}>
      <style>{`
        .pjh2-hero {
          position: relative;
          padding: 8.5rem 2rem 0;
          margin-top: 70px;
          background: #FAF9F7;
          overflow: hidden;
        }

        .pjh2-grid-lines {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(28,26,38,0.035) 1px, transparent 1px),
            linear-gradient(90deg, rgba(28,26,38,0.035) 1px, transparent 1px);
          background-size: 64px 64px;
          mask-image: radial-gradient(ellipse 70% 60% at 50% 20%, #000 30%, transparent 75%);
          pointer-events: none;
        }

        .pjh2-inner {
          position: relative;
          z-index: 2;
          max-width: 860px;
          margin: 0 auto;
          text-align: center;
        }

        .pjh2-kicker {
          display: inline-flex;
          align-items: center;
          gap: 0.55rem;
          font-size: 0.78rem;
          font-weight: 600;
          letter-spacing: 0.4px;
          color: #6B6780;
          font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
          opacity: 0;
          transform: translateY(8px);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }

        .pjh2-hero.is-in .pjh2-kicker {
          opacity: 1;
          transform: translateY(0);
        }

        .pjh2-kicker-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #2DD4A7;
          box-shadow: 0 0 0 3px rgba(45,212,167,0.18);
        }

        .pjh2-title {
          font-size: clamp(2.6rem, 6vw, 4.6rem);
          font-weight: 750;
          line-height: 1.04;
          letter-spacing: -0.045em;
          color: #18161F;
          margin: 1.3rem 0 1.25rem;
        }

        .pjh2-title span {
          display: inline-block;
          opacity: 0;
          filter: blur(6px);
          transform: translateY(14px);
          transition: opacity 0.6s cubic-bezier(0.16,1,0.3,1), transform 0.6s cubic-bezier(0.16,1,0.3,1), filter 0.6s cubic-bezier(0.16,1,0.3,1);
        }

        .pjh2-hero.is-in .pjh2-title span {
          opacity: 1;
          filter: blur(0);
          transform: translateY(0);
        }

        .pjh2-title .pjh2-accent {
          color: #6D5BD0;
        }

        .pjh2-subtitle {
          font-size: 1.08rem;
          line-height: 1.6;
          color: #6B6780;
          max-width: 560px;
          margin: 0 auto 2.2rem;
          opacity: 0;
          transform: translateY(10px);
          transition: opacity 0.6s ease 0.32s, transform 0.6s ease 0.32s;
        }

        .pjh2-hero.is-in .pjh2-subtitle {
          opacity: 1;
          transform: translateY(0);
        }

        .pjh2-cta-row {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.9rem;
          margin-bottom: 3.5rem;
          opacity: 0;
          transform: translateY(10px);
          transition: opacity 0.6s ease 0.42s, transform 0.6s ease 0.42s;
        }

        .pjh2-hero.is-in .pjh2-cta-row {
          opacity: 1;
          transform: translateY(0);
        }

        .pjh2-cta-primary {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.72rem 1.5rem;
          background: #18161F;
          color: #FAF9F7;
          font-size: 0.88rem;
          font-weight: 600;
          border-radius: 0.6rem;
          text-decoration: none;
          border: none;
          cursor: pointer;
          transition: transform 0.25s cubic-bezier(0.16,1,0.3,1), background 0.25s ease;
        }

        .pjh2-cta-primary:hover {
          background: #2D2A3A;
          transform: translateY(-2px);
        }

        .pjh2-cta-ghost {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.72rem 1.4rem;
          background: transparent;
          color: #18161F;
          font-size: 0.88rem;
          font-weight: 600;
          border-radius: 0.6rem;
          border: 1px solid rgba(28,26,38,0.14);
          cursor: pointer;
          transition: border-color 0.25s ease, background 0.25s ease, transform 0.25s cubic-bezier(0.16,1,0.3,1);
        }

        .pjh2-cta-ghost:hover {
          border-color: rgba(28,26,38,0.3);
          background: rgba(28,26,38,0.03);
          transform: translateY(-2px);
        }

        /* ===== Stat strip ===== */
        .pjh2-stat-strip {
          position: relative;
          z-index: 2;
          max-width: 980px;
          margin: 0 auto;
          border: 1px solid rgba(28,26,38,0.09);
          border-bottom: none;
          border-radius: 1rem 1rem 0 0;
          background: #FFFFFF;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          opacity: 0;
          transform: translateY(16px);
          transition: opacity 0.7s ease 0.5s, transform 0.7s ease 0.5s;
        }

        .pjh2-hero.is-in .pjh2-stat-strip {
          opacity: 1;
          transform: translateY(0);
        }

        .pjh2-stat-cell {
          padding: 1.7rem 1rem;
          text-align: center;
          border-left: 1px solid rgba(28,26,38,0.08);
        }

        .pjh2-stat-cell:first-child {
          border-left: none;
        }

        .pjh2-stat-value {
          font-size: 1.9rem;
          font-weight: 800;
          color: #18161F;
          letter-spacing: -0.02em;
          font-variant-numeric: tabular-nums;
        }

        .pjh2-stat-cell:nth-child(1) .pjh2-stat-value { color: #6D5BD0; }
        .pjh2-stat-cell:nth-child(2) .pjh2-stat-value { color: #0E9E78; }
        .pjh2-stat-cell:nth-child(3) .pjh2-stat-value { color: #C2740B; }
        .pjh2-stat-cell:nth-child(4) .pjh2-stat-value { color: #3B7FC4; }

        .pjh2-stat-label {
          font-size: 0.72rem;
          color: #8B879C;
          margin-top: 0.3rem;
          font-weight: 600;
          letter-spacing: 0.2px;
        }

        /* ===== Ticker ===== */
        .pjh2-ticker-wrap {
          position: relative;
          z-index: 2;
          max-width: 980px;
          margin: 0 auto;
          border: 1px solid rgba(28,26,38,0.09);
          border-top: 1px dashed rgba(28,26,38,0.12);
          border-radius: 0 0 1rem 1rem;
          background: #FFFFFF;
          overflow: hidden;
          padding: 1.1rem 0;
          opacity: 0;
          transition: opacity 0.7s ease 0.62s;
        }

        .pjh2-hero.is-in .pjh2-ticker-wrap {
          opacity: 1;
        }

        .pjh2-ticker-label {
          text-align: center;
          font-size: 0.66rem;
          font-weight: 700;
          letter-spacing: 1.6px;
          text-transform: uppercase;
          color: #B6B2C8;
          margin-bottom: 0.8rem;
        }

        .pjh2-ticker-mask {
          -webkit-mask-image: linear-gradient(90deg, transparent 0%, #000 8%, #000 92%, transparent 100%);
          mask-image: linear-gradient(90deg, transparent 0%, #000 8%, #000 92%, transparent 100%);
        }

        .pjh2-ticker-track {
          display: flex;
          width: max-content;
          gap: 2.4rem;
          animation: pjh2Scroll 26s linear infinite;
        }

        .pjh2-ticker-wrap:hover .pjh2-ticker-track {
          animation-play-state: paused;
        }

        @keyframes pjh2Scroll {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }

        .pjh2-ticker-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.85rem;
          font-weight: 600;
          color: #4B4760;
          white-space: nowrap;
        }

        .pjh2-ticker-item::before {
          content: '';
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: #C9C5DA;
        }

        .pjh2-bottom-fade {
          height: 3.5rem;
        }

        @media (max-width: 768px) {
          .pjh2-hero {
            padding: 6.5rem 1.25rem 0;
          }

          .pjh2-title {
            letter-spacing: -0.03em;
          }

          .pjh2-stat-strip {
            grid-template-columns: repeat(2, 1fr);
          }

          .pjh2-stat-cell:nth-child(3) {
            border-left: none;
          }

          .pjh2-stat-cell:nth-child(1),
          .pjh2-stat-cell:nth-child(3) {
            border-left: none;
          }

          .pjh2-stat-cell:nth-child(2),
          .pjh2-stat-cell:nth-child(4) {
            border-left: 1px solid rgba(28,26,38,0.08);
          }

          .pjh2-stat-cell {
            padding: 1.3rem 0.5rem;
            border-bottom: 1px solid rgba(28,26,38,0.08);
          }

          .pjh2-stat-cell:nth-child(3),
          .pjh2-stat-cell:nth-child(4) {
            border-bottom: none;
          }

          .pjh2-cta-row {
            flex-direction: column;
            width: 100%;
          }

          .pjh2-cta-primary, .pjh2-cta-ghost {
            width: 100%;
            justify-content: center;
          }
        }

        @media (max-width: 480px) {
          .pjh2-stat-value { font-size: 1.55rem; }
          .pjh2-ticker-item { font-size: 0.78rem; }
        }

        @media (prefers-reduced-motion: reduce) {
          .pjh2-ticker-track { animation: none; }
          .pjh2-kicker, .pjh2-title span, .pjh2-subtitle, .pjh2-cta-row, .pjh2-stat-strip, .pjh2-ticker-wrap {
            opacity: 1 !important;
            transform: none !important;
            filter: none !important;
            transition: none !important;
          }
        }
      `}</style>

      <div className="pjh2-grid-lines"></div>

      <div className="pjh2-inner">
        <span className="pjh2-kicker">
          <span className="pjh2-kicker-dot"></span>
          BELLARY INFO TECH SOLUTIONS — SELECTED WORK
        </span>

        <h1 className="pjh2-title">
          <span>Engineering</span> <span>built</span> <span>for</span>{' '}
          <span className="pjh2-accent">scale,</span> <span>shipped</span> <span>with</span>{' '}
          <span>care.</span>
        </h1>

        <p className="pjh2-subtitle">
          A portfolio of travel platforms, fintech tools, and enterprise billing systems —
          each one designed, built, and shipped by a team that treats every client's product
          like its own.
        </p>

        <div className="pjh2-cta-row">
          <button className="pjh2-cta-primary">
            Explore Projects <i className="bi bi-arrow-down"></i>
          </button>
          <button className="pjh2-cta-ghost">
            Start a Project <i className="bi bi-arrow-up-right"></i>
          </button>
        </div>
      </div>

      <div className="pjh2-stat-strip">
        {stats.map((s, i) => (
          <div key={i} className="pjh2-stat-cell">
            <div className="pjh2-stat-value">{countedValues[i]}{s.suffix}</div>
            <div className="pjh2-stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="pjh2-ticker-wrap">
        <div className="pjh2-ticker-label">Projects Delivered</div>
        <div className="pjh2-ticker-mask">
          <div className="pjh2-ticker-track">
            {[...tickerItems, ...tickerItems].map((name, i) => (
              <span key={i} className="pjh2-ticker-item">{name}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="pjh2-bottom-fade"></div>
    </section>
  );
};

export default ProjectsMain;